import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Tabs,
    Tab,
    List,
    ListItemButton,
    Divider,
    Button,
    CircularProgress,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    FormLabel,
    Alert,
    Snackbar,
    Avatar,
    RadioGroup,
    Radio,
    FormControlLabel,
    Skeleton
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

import { getEmployerInterviews, cancelInterviewApi, rescheduleInterviewApi, updateInterviewStatusApi } from '../../Api/InterviewsApi';
import { updateCandidateStatus } from '../../Api/CandidateApi';

const BRAND = '#6B31C4';
const BRAND_DARK = '#5a29a5';

const DURATION_OPTIONS = [
    { label: '15 minutes', value: 15 },
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
];
const HOURS_12 = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTES = ['00', '15', '30', '45'];
const TIMEZONES = ['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London'];

// Safely parses MySQL datetime values in local browser time zone
const parseAsLocal = (dateString) => {
    if (!dateString) return new Date(NaN);
    return new Date(dateString);
};

// Formatting helper to match mockup format exactly (e.g. Wednesday 10 September, 2025)
const formatProposedDay = (dateString) => {
    if (!dateString) return '';
    const date = parseAsLocal(dateString);
    if (isNaN(date.getTime())) return '';
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options).replace(/,/g, '');
};

// Formatting helper to match mockup time format (e.g. 9 to 9:30 am (IST))
const formatProposedTime = (dateString, durationMinutes = 30, tz = 'IST') => {
    if (!dateString) return '';
    const start = parseAsLocal(dateString);
    if (isNaN(start.getTime())) return '';
    
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    
    const formatTimePart = (d) => {
        let hours = d.getHours();
        const minutes = d.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12; // hour '0' should be '12'
        const minStr = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
        return { display: `${hours}${minStr}`, ampm };
    };

    const startFormatted = formatTimePart(start);
    const endFormatted = formatTimePart(end);

    if (startFormatted.ampm === endFormatted.ampm) {
        return `${startFormatted.display} to ${endFormatted.display} ${endFormatted.ampm} (${tz})`;
    } else {
        return `${startFormatted.display} ${startFormatted.ampm} to ${endFormatted.display} ${endFormatted.ampm} (${tz})`;
    }
};

const GetInterviews = () => {
    const [tabVal, setTabVal] = useState(0); // 0: Upcoming, 1: Pending, 2: Past
    const [allInterviews, setAllInterviews] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [counts, setCounts] = useState({ upcoming: 0, pending: 0, past: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Notes state (saved locally)
    const [notes, setNotes] = useState('');

    // Reschedule state
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [rescheduleForm, setRescheduleForm] = useState({
        date: '', hour: '10', minute: '00', period: 'AM', durationMinutes: 30, message: '', mode: 'online', timeZone: 'Asia/Kolkata'
    });
    const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
    const [rescheduleError, setRescheduleError] = useState('');

    // Cancel state
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    // Complete state
    const [completeOpen, setCompleteOpen] = useState(false);
    const [candidateStatus, setCandidateStatus] = useState('keep');
    const [completeSubmitting, setCompleteSubmitting] = useState(false);
    const [completeError, setCompleteError] = useState('');

    // Notification snackbar
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Get all categorized categories
            const data = await getEmployerInterviews('all');
            if (data.success) {
                setAllInterviews(data.interviews || []);
                setCounts({
                    upcoming: data.counts?.upcoming || 0,
                    pending: data.counts?.pending || 0,
                    past: data.counts?.past || 0
                });
            } else {
                setError('Failed to fetch interviews');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch interviews');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Filter list based on selected tab value
    useEffect(() => {
        const now = new Date();
        let list = [];
        if (tabVal === 0) { // Upcoming: status scheduled/rescheduled & start > now
            list = allInterviews.filter(iv => 
                (iv.status === 'scheduled' || iv.status === 'rescheduled') && 
                parseAsLocal(iv.interviewStartAt) > now
            );
        } else if (tabVal === 1) { // Pending: status requested / fallback
            list = allInterviews.filter(iv => iv.status === 'requested');
        } else if (tabVal === 2) { // Past: status completed or start <= now or cancelled
            list = allInterviews.filter(iv => 
                iv.status === 'completed' || 
                iv.status === 'cancelled' || 
                parseAsLocal(iv.interviewStartAt) <= now
            );
        }
        setFilteredList(list);

        // Auto-select first item if list changes
        if (list.length > 0) {
            setSelectedId(list[0].id);
        } else {
            setSelectedId(null);
        }
    }, [allInterviews, tabVal]);

    const activeInterview = allInterviews.find(iv => iv.id === selectedId);

    // Sync notes with localStorage when selected interview changes
    useEffect(() => {
        if (selectedId) {
            const savedNotes = localStorage.getItem(`interview_notes_${selectedId}`);
            setNotes(savedNotes || '');
        } else {
            setNotes('');
        }
    }, [selectedId]);

    const handleNotesChange = (e) => {
        const text = e.target.value;
        setNotes(text);
        if (selectedId) {
            localStorage.setItem(`interview_notes_${selectedId}`, text);
        }
    };

    const getInterviewModeTitle = (mode) => {
        const lower = (mode || '').toLowerCase();
        if (lower === 'phone') return 'Phone Interview';
        if (lower === 'in-person') return 'In-person Interview';
        return 'Video Interview';
    };

    const getInterviewIcon = (mode, color = BRAND) => {
        const lower = (mode || '').toLowerCase();
        if (lower === 'phone') return <PhoneIcon sx={{ color, fontSize: 20 }} />;
        if (lower === 'in-person') return <BusinessIcon sx={{ color, fontSize: 20 }} />;
        return <VideocamIcon sx={{ color, fontSize: 20 }} />;
    };

    const handleCancelClick = async () => {
        if (!selectedId || window.confirm('Are you sure you want to cancel this interview?')) {
            setCancelSubmitting(true);
            try {
                await cancelInterviewApi(selectedId);
                setSnack({ open: true, msg: 'Interview cancelled successfully!', severity: 'success' });
                fetchAllData();
            } catch (err) {
                setSnack({ open: true, msg: err?.response?.data?.message || 'Failed to cancel interview.', severity: 'error' });
            } finally {
                setCancelSubmitting(false);
            }
        }
    };

    const handleCompleteClick = () => {
        setCompleteOpen(true);
        setCandidateStatus('keep');
        setCompleteError('');
    };

    const submitCompleteInterview = async () => {
        if (!selectedId || !activeInterview) return;
        setCompleteSubmitting(true);
        setCompleteError('');
        try {
            // 1. Update interview status to completed
            await updateInterviewStatusApi(selectedId, 'completed');

            // 2. Update candidate status if not 'keep'
            if (candidateStatus !== 'keep' && activeInterview.application?.id) {
                await updateCandidateStatus(activeInterview.application.id, candidateStatus);
            }

            setSnack({ open: true, msg: 'Interview marked as completed successfully!', severity: 'success' });
            setCompleteOpen(false);
            fetchAllData();
        } catch (err) {
            setCompleteError(err?.response?.data?.message || 'Failed to complete interview.');
        } finally {
            setCompleteSubmitting(false);
        }
    };

    const openRescheduleModal = () => {
        if (!activeInterview) return;
        const currentStart = parseAsLocal(activeInterview.interviewStartAt);
        const yyyy = currentStart.getFullYear();
        const mm = String(currentStart.getMonth() + 1).padStart(2, '0');
        const dd = String(currentStart.getDate()).padStart(2, '0');
        
        let rawHour = currentStart.getHours();
        const period = rawHour >= 12 ? 'PM' : 'AM';
        let hour12 = rawHour % 12;
        if (hour12 === 0) hour12 = 12;
        
        setRescheduleForm({
            date: `${yyyy}-${mm}-${dd}`,
            hour: String(hour12).padStart(2, '0'),
            minute: String(currentStart.getMinutes()).padStart(2, '0'),
            period: period,
            durationMinutes: activeInterview.durationMinutes || 30,
            message: activeInterview.message || '',
            mode: activeInterview.mode || 'online',
            timeZone: activeInterview.timeZone || 'Asia/Kolkata'
        });
        setRescheduleError('');
        setRescheduleOpen(true);
    };

    const handleRescheduleSubmit = async () => {
        if (!rescheduleForm.date || !rescheduleForm.message) {
            setRescheduleError('Date and message are required.');
            return;
        }

        const hourVal = parseInt(rescheduleForm.hour, 10);
        let hour24 = hourVal;
        if (rescheduleForm.period === 'PM' && hourVal !== 12) {
            hour24 += 12;
        } else if (rescheduleForm.period === 'AM' && hourVal === 12) {
            hour24 = 0;
        }
        const hourStr = String(hour24).padStart(2, '0');

        const selectedStart = new Date(`${rescheduleForm.date}T${hourStr}:${rescheduleForm.minute}:00`);
        const now = new Date();
        selectedStart.setSeconds(0, 0);
        now.setSeconds(0, 0);
        if (selectedStart < now) {
            setRescheduleError('Cannot schedule interviews in the past.');
            return;
        }

        setRescheduleSubmitting(true);
        setRescheduleError('');
        try {
            const startAt = selectedStart.toISOString();
            await rescheduleInterviewApi(selectedId, {
                interviewStartAt: startAt,
                durationMinutes: Number(rescheduleForm.durationMinutes),
                mode: rescheduleForm.mode,
                timeZone: rescheduleForm.timeZone,
                message: rescheduleForm.message
            });
            setSnack({ open: true, msg: 'Interview rescheduled successfully!', severity: 'success' });
            setRescheduleOpen(false);
            fetchAllData();
        } catch (err) {
            setRescheduleError(err?.response?.data?.message || 'Failed to reschedule interview. Please try again.');
        } finally {
            setRescheduleSubmitting(false);
        }
    };

    const tabLabelStyle = (count) => ({
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.85rem',
        minWidth: 'auto',
        px: 2
    });

    return (
        <Box sx={{ backgroundColor: '#F8F9FA', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                    Interviews
                </Typography>

                {loading ? (
                    <Grid container spacing={3} sx={{ minHeight: 'calc(100vh - 180px)' }}>
                        {/* LEFT COLUMN: Sidebar Navigation Placeholder */}
                        <Grid size={{ xs: 12, md: 4 }} sx={{ borderRight: '1px solid #E5E7EB', pr: { md: 4 } }}>
                            <Box sx={{ borderBottom: '1px solid #E5E7EB', mb: 2, display: 'flex', gap: 2 }}>
                                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                            </Box>
                            
                            <List sx={{ width: '100%', p: 0 }}>
                                {[1, 2, 3].map((item) => (
                                    <Box key={item} sx={{ p: 2, borderRadius: 2, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #E5E7EB' }}>
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                            <Skeleton variant="circular" width={40} height={40} />
                                            <Box>
                                                <Skeleton variant="text" width={100} height={20} />
                                                <Skeleton variant="text" width={140} height={16} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Skeleton variant="text" width={50} height={18} />
                                            <Skeleton variant="text" width={70} height={14} />
                                        </Box>
                                    </Box>
                                ))}
                            </List>
                        </Grid>

                        {/* RIGHT COLUMN: Details View Placeholder */}
                        <Grid size={{ xs: 12, md: 8 }} sx={{ pl: { md: 4 } }}>
                            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #E5E7EB', height: '100%', bgcolor: '#ffffff' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                                    <Skeleton variant="circular" width={60} height={60} />
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Skeleton variant="text" width="40%" height={28} />
                                        <Skeleton variant="text" width="20%" height={18} />
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <Box>
                                        <Skeleton variant="text" width="20%" height={16} sx={{ mb: 0.5 }} />
                                        <Skeleton variant="text" width="60%" height={24} />
                                    </Box>
                                    <Box>
                                        <Skeleton variant="text" width="20%" height={16} sx={{ mb: 0.5 }} />
                                        <Skeleton variant="text" width="30%" height={24} />
                                    </Box>
                                    <Box>
                                        <Skeleton variant="text" width="20%" height={16} sx={{ mb: 0.5 }} />
                                        <Skeleton variant="text" width="80%" height={24} />
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 5, display: 'flex', gap: 2 }}>
                                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '100px' }} />
                                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '100px' }} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
                ) : (
                    <Grid container spacing={3} sx={{ minHeight: 'calc(100vh - 180px)' }}>
                        
                        {/* LEFT COLUMN: Sidebar Navigation */}
                        <Grid size={{ xs: 12, md: 4 }} sx={{ borderRight: '1px solid #E5E7EB', pr: { md: 4 } }}>
                            <Box sx={{ borderBottom: '1px solid #E5E7EB', mb: 2 }}>
                                <Tabs 
                                    value={tabVal} 
                                    onChange={(_, v) => setTabVal(v)}
                                    TabIndicatorProps={{ style: { backgroundColor: BRAND, height: 3, borderRadius: 2 } }}
                                    sx={{ minHeight: 40 }}
                                >
                                    <Tab label={`Upcoming (${counts.upcoming})`} sx={tabLabelStyle(counts.upcoming)} />
                                    <Tab label={`Pending (${counts.pending})`} sx={tabLabelStyle(counts.pending)} />
                                    <Tab label={`Completed (${counts.past})`} sx={tabLabelStyle(counts.past)} />
                                </Tabs>
                            </Box>

                            {filteredList.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2">No interviews in this section.</Typography>
                                </Box>
                            ) : (
                                <List sx={{ width: '100%', p: 0 }}>
                                    {filteredList.map((iv) => {
                                        const candidateName = iv.application?.candidate?.name || 'Unknown Candidate';
                                        const jobTitle = iv.job?.title || 'Job Role';
                                        const startAtStr = iv.interviewStartAt;
                                        
                                        const isSelected = selectedId === iv.id;
                                        const formattedDate = startAtStr ? parseAsLocal(startAtStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                                        const timeRange = formatProposedTime(startAtStr, iv.durationMinutes);

                                        return (
                                            <React.Fragment key={iv.id}>
                                                <ListItemButton 
                                                    selected={isSelected}
                                                    onClick={() => setSelectedId(iv.id)}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2,
                                                        mb: 1.5,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        border: isSelected ? `1.5px solid ${BRAND}` : '1.5px solid #f0f2f5',
                                                        backgroundColor: isSelected ? `${BRAND}08` : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: isSelected ? `${BRAND}12` : '#f8f9fa'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, pr: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1c1c1c', mb: 0.5 }}>
                                                            {getInterviewModeTitle(iv.mode)}
                                                        </Typography>
                                                        
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: '#555' }}>
                                                            <PersonIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                                                {candidateName}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: '#555' }}>
                                                            <CalendarMonthIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                                {formattedDate} | {timeRange}
                                                            </Typography>
                                                        </Box>

                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                            {jobTitle}
                                                        </Typography>
                                                    </Box>

                                                    <Avatar sx={{ bgcolor: `${BRAND}15`, width: 36, height: 36 }}>
                                                        {getInterviewIcon(iv.mode)}
                                                    </Avatar>
                                                </ListItemButton>
                                                <Divider sx={{ my: 1, borderColor: '#f8f9fa' }} />
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </Grid>

                        {/* RIGHT COLUMN: Interview Detail & Notes */}
                        <Grid size={{ xs: 12, md: 8 }} sx={{ pl: { md: 4 }, display: 'flex', flexDirection: 'column' }}>
                            {activeInterview ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    
                                    {/* Candidate & Type Info */}
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1c1c1c', mb: 1 }}>
                                            {activeInterview.mode === 'phone' ? 'Phone call' : activeInterview.mode === 'in-person' ? 'In-person interview' : 'Video meeting'} with {activeInterview.application?.candidate?.name || 'Candidate'}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#555' }}>
                                            <CalendarMonthIcon sx={{ fontSize: 18, color: '#666' }} />
                                            <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                                                Time proposed for {formatProposedDay(activeInterview.interviewStartAt)} from {formatProposedTime(activeInterview.interviewStartAt, activeInterview.durationMinutes)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={openRescheduleModal}
                                            sx={{
                                                textTransform: 'none',
                                                borderColor: BRAND,
                                                color: BRAND,
                                                borderRadius: 2,
                                                px: 3,
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: BRAND_DARK,
                                                    backgroundColor: `${BRAND}05`
                                                }
                                            }}
                                        >
                                            Reschedule
                                        </Button>

                                        {activeInterview.status !== 'completed' && activeInterview.status !== 'cancelled' && (
                                            <Button 
                                                variant="contained"
                                                onClick={handleCompleteClick}
                                                sx={{
                                                    textTransform: 'none',
                                                    backgroundColor: '#2e7d32',
                                                    color: '#fff',
                                                    borderRadius: 2,
                                                    px: 3,
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: '#1b5e20'
                                                    }
                                                }}
                                            >
                                                Complete Interview
                                            </Button>
                                        )}
                                        
                                        <Button 
                                            variant="outlined"
                                            onClick={handleCancelClick}
                                            disabled={cancelSubmitting}
                                            sx={{
                                                textTransform: 'none',
                                                borderColor: '#ff4d4d',
                                                color: '#ff4d4d',
                                                borderRadius: 2,
                                                px: 3,
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: '#cc0000',
                                                    backgroundColor: 'rgba(255, 77, 77, 0.05)'
                                                }
                                            }}
                                        >
                                            {cancelSubmitting ? 'Canceling...' : 'Cancel'}
                                        </Button>
                                    </Box>

                                    {/* Take Notes Area */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1c1c1c', mb: 1 }}>
                                            Take Notes
                                        </Typography>
                                        <TextField 
                                            fullWidth
                                            multiline
                                            rows={12}
                                            placeholder="Write down meeting notes, candidate impressions, or question outlines here..."
                                            value={notes}
                                            onChange={handleNotesChange}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    backgroundColor: '#fafafa',
                                                    '& fieldset': { borderColor: '#e2e8f0' },
                                                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                                                    '&.Mui-focused fieldset': { borderColor: BRAND }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, minHeight: 300, color: 'text.secondary' }}>
                                    <CalendarMonthIcon sx={{ fontSize: 60, opacity: 0.15, mb: 1 }} />
                                    <Typography>Select an interview from the list to view details.</Typography>
                                </Box>
                            )}
                        </Grid>

                    </Grid>
                )}
            </Container>

            {/* Reschedule Modal */}
            <Dialog 
                open={rescheduleOpen} 
                onClose={() => setRescheduleOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Reschedule Interview</DialogTitle>
                <DialogContent>
                    {rescheduleError && <Alert severity="error" sx={{ mb: 2 }}>{rescheduleError}</Alert>}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Duration *</FormLabel>
                            <Select 
                                size="small" 
                                value={rescheduleForm.durationMinutes} 
                                onChange={e => setRescheduleForm(f => ({ ...f, durationMinutes: e.target.value }))}
                            >
                                {DURATION_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Mode *</FormLabel>
                            <Select 
                                size="small" 
                                value={rescheduleForm.mode} 
                                onChange={e => setRescheduleForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <MenuItem value="online">Online / Video</MenuItem>
                                <MenuItem value="phone">Phone call</MenuItem>
                                <MenuItem value="in-person">In-person</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ flex: 2 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Date *</FormLabel>
                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    type="date" 
                                    value={rescheduleForm.date}
                                    onChange={e => setRescheduleForm(f => ({ ...f, date: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} 
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Hour *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={rescheduleForm.hour} 
                                        onChange={e => setRescheduleForm(f => ({ ...f, hour: e.target.value }))}
                                    >
                                        {HOURS_12.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Minute *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={rescheduleForm.minute} 
                                        onChange={e => setRescheduleForm(f => ({ ...f, minute: e.target.value }))}
                                    >
                                        {MINUTES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>AM/PM *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={rescheduleForm.period} 
                                        onChange={e => setRescheduleForm(f => ({ ...f, period: e.target.value }))}
                                    >
                                        <MenuItem value="AM">AM</MenuItem>
                                        <MenuItem value="PM">PM</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Time Zone *</FormLabel>
                            <Select 
                                size="small" 
                                value={rescheduleForm.timeZone} 
                                onChange={e => setRescheduleForm(f => ({ ...f, timeZone: e.target.value }))}
                            >
                                {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Personal Note *</FormLabel>
                            <TextField 
                                fullWidth 
                                multiline 
                                rows={3} 
                                size="small" 
                                placeholder="State reason or add new instructions..."
                                value={rescheduleForm.message} 
                                onChange={e => setRescheduleForm(f => ({ ...f, message: e.target.value }))} 
                            />
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRescheduleOpen(false)} sx={{ textTransform: 'none', color: '#555' }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        disableElevation 
                        onClick={handleRescheduleSubmit}
                        disabled={rescheduleSubmitting}
                        sx={{ 
                            textTransform: 'none', 
                            bgcolor: BRAND, 
                            borderRadius: 2,
                            '&:hover': { bgcolor: BRAND_DARK } 
                        }}
                    >
                        {rescheduleSubmitting ? 'Updating...' : 'Reschedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Interview Dialog */}
            <Dialog 
                open={completeOpen} 
                onClose={() => setCompleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Complete Interview</DialogTitle>
                <DialogContent>
                    {completeError && <Alert severity="error" sx={{ mb: 2 }}>{completeError}</Alert>}
                    
                    <Typography variant="body1" sx={{ color: '#333', mb: 3 }}>
                        Are you sure you want to mark this interview as completed?
                    </Typography>

                    <FormControl component="fieldset" fullWidth>
                        <FormLabel sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1.5, color: '#1c1c1c' }}>
                            Update Candidate Status:
                        </FormLabel>
                        <RadioGroup 
                            value={candidateStatus} 
                            onChange={e => setCandidateStatus(e.target.value)}
                        >
                            <FormControlLabel value="keep" control={<Radio color="primary" />} label="Keep current status (No change)" />
                            <FormControlLabel value="shortlisted" control={<Radio color="warning" />} label="Passed / Shortlisted" />
                            <FormControlLabel value="hired" control={<Radio color="success" />} label="Hired" />
                            <FormControlLabel value="rejected" control={<Radio color="error" />} label="Rejected" />
                            <FormControlLabel value="reviewed" control={<Radio color="secondary" />} label="Reviewed" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCompleteOpen(false)} sx={{ textTransform: 'none', color: '#555' }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        disableElevation 
                        onClick={submitCompleteInterview}
                        disabled={completeSubmitting}
                        sx={{ 
                            textTransform: 'none', 
                            bgcolor: '#2e7d32', 
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#1b5e20' } 
                        }}
                    >
                        {completeSubmitting ? 'Submitting...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snack */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default GetInterviews;